// teamSlice.js

import {
  createSlice,
  createAsyncThunk,
} from '@reduxjs/toolkit';

import { toast } from 'react-toastify';

const API_URL =
  process.env.REACT_APP_API_URL;

/* =========================
   CREATE
========================= */

export const createTeamMember =
  createAsyncThunk(
    'team/create',
    async (formData, thunkAPI) => {
      try {
        const response = await fetch(
          `${API_URL}/create-team`,
          {
            method: 'POST',
            body: formData,
          }
        );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              'Failed to create'
          );
        }

        toast.success(
          'Team member created'
        );

        return (
          data.teamMember ||
          data.data
        );
      } catch (error) {
        toast.error(error.message);

        return thunkAPI.rejectWithValue(
          error.message
        );
      }
    }
  );

/* =========================
   FETCH
========================= */

export const fetchTeamMembers =
  createAsyncThunk(
    'team/fetchAll',
    async (superAdminId, thunkAPI) => {
      try {
        const response = await fetch(
          `${API_URL}/get-all-team?superAdminId`
        );

        const data =
          await response.json();

        console.log(
          'TEAM API RESPONSE:',
          data
        );

        if (!response.ok) {
          throw new Error(
            data.message ||
              'Failed to fetch'
          );
        }

        const finalData =
          data?.teamMembers ||
          data?.teams ||
          data?.data ||
          data?.result ||
          [];

        return Array.isArray(
          finalData
        )
          ? finalData
          : [];
      } catch (error) {
        toast.error(error.message);

        return thunkAPI.rejectWithValue(
          error.message
        );
      }
    }
  );

/* =========================
   UPDATE
========================= */

export const updateTeamMember =
  createAsyncThunk(
    'team/update',
    async (
      { id, formData },
      thunkAPI
    ) => {
      try {
        const response = await fetch(
          `${API_URL}/update-team/${id}`,
          {
            method: 'PUT',
            body: formData,
          }
        );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              'Failed to update'
          );
        }

        toast.success(
          'Team member updated'
        );

        return (
          data.teamMember ||
          data.data
        );
      } catch (error) {
        toast.error(error.message);

        return thunkAPI.rejectWithValue(
          error.message
        );
      }
    }
  );

/* =========================
   DELETE
========================= */

export const deleteTeamMember =
  createAsyncThunk(
    'team/delete',
    async (id, thunkAPI) => {
      try {
        const response = await fetch(
          `${API_URL}/delete-team/${id}`,
          {
            method: 'DELETE',
          }
        );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              'Failed to delete'
          );
        }

        toast.success(
          'Team member deleted'
        );

        return id;
      } catch (error) {
        toast.error(error.message);

        return thunkAPI.rejectWithValue(
          error.message
        );
      }
    }
  );

/* =========================
   SLICE
========================= */

const teamSlice = createSlice({
  name: 'team',

  initialState: {
    list: [],
    loading: false,
    error: null,
  },

  reducers: {},

  extraReducers: (builder) => {
    builder

      /* FETCH */

      .addCase(
        fetchTeamMembers.pending,
        (state) => {
          state.loading = true;
        }
      )

      .addCase(
        fetchTeamMembers.fulfilled,
        (state, action) => {
          state.loading = false;
          state.list = action.payload;
        }
      )

      .addCase(
        fetchTeamMembers.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      /* CREATE */

      .addCase(
        createTeamMember.fulfilled,
        (state, action) => {
          state.list.unshift(
            action.payload
          );
        }
      )

      /* UPDATE */

      .addCase(
        updateTeamMember.fulfilled,
        (state, action) => {
          const updatedId =
            action.payload?._id ||
            action.payload?.id;

          const index =
            state.list.findIndex(
              (member) =>
                (member._id ||
                  member.id) ===
                updatedId
            );

          if (index !== -1) {
            state.list[index] =
              action.payload;
          }
        }
      )

      /* DELETE */

      .addCase(
        deleteTeamMember.fulfilled,
        (state, action) => {
          state.list =
            state.list.filter(
              (member) =>
                (member._id ||
                  member.id) !==
                action.payload
            );
        }
      );
  },
});

export default teamSlice.reducer;